import { useCallback, useMemo } from "react";
import usePersistState from "./usePersistState";
import { STORAGE_KEYS } from "@utils/localStorage";

interface BodySystemPreferences {
  order: string[];
  hidden: string[];
}

interface BodySystem {
  _id: string;
  name: string;
  icon?: unknown;
  flow: string;
}

const DEFAULT_PREFERENCES: BodySystemPreferences = {
  order: [],
  hidden: [],
};

const useBodySystemPreferences = () => {
  const [preferences, setPreferences] = usePersistState<BodySystemPreferences>(
    STORAGE_KEYS.BODY_SYSTEM_PREFERENCES,
    DEFAULT_PREFERENCES
  );

  const isHidden = useCallback(
    (name: string) => preferences.hidden.includes(name),
    [preferences.hidden]
  );

  const toggleHidden = useCallback(
    (name: string) => {
      setPreferences({
        ...preferences,
        hidden: preferences.hidden.includes(name)
          ? preferences.hidden.filter((n) => n !== name)
          : [...preferences.hidden, name],
      });
    },
    [preferences, setPreferences]
  );

  const reorder = useCallback(
    (newOrder: string[]) => {
      setPreferences({
        ...preferences,
        order: newOrder,
      });
    },
    [preferences, setPreferences]
  );

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
  }, [setPreferences]);

  const hasCustomPreferences = useMemo(
    () => preferences.order.length > 0 || preferences.hidden.length > 0,
    [preferences]
  );

  const hiddenCount = useMemo(
    () => preferences.hidden.length,
    [preferences.hidden]
  );

  const applyPreferences = useCallback(
    (bodySystems: BodySystem[], includeHidden = false): BodySystem[] => {
      const { order, hidden } = preferences;

      // Filter hidden items (unless in edit mode)
      let result = includeHidden
        ? bodySystems
        : bodySystems.filter((bs) => !hidden.includes(bs.name));

      // Sort by user order if any custom order exists
      if (order.length > 0) {
        result = [...result].sort((a, b) => {
          const indexA = order.indexOf(a.name);
          const indexB = order.indexOf(b.name);
          // Items not in the order list go to the end in their original position
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      }

      return result;
    },
    [preferences]
  );

  return {
    reorder,
    toggleHidden,
    resetPreferences,
    isHidden,
    applyPreferences,
    hasCustomPreferences,
    hiddenCount,
  };
};

export default useBodySystemPreferences;